import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import 'daterangepicker/daterangepicker.css';
import $ from 'jquery';
import 'daterangepicker';
import moment from 'moment';
import Calendar from '../../pictures/calendar-datetime.svg';
import * as styles from './DateRangePicker.module.scss';

import './DateRangePicker.scss';

const getTimeRanges = (inputValue, ranges) => {
  let start; let end;

  if (inputValue.includes(':')) {
    // Custom range with unix timestamps
    const [startUnix, endUnix] = inputValue.split(':').map(Number);
    start = moment.unix(startUnix);
    end = moment.unix(endUnix);
  } else {
    // Predefined ranges
    switch (inputValue) {
      case '1d':
        [start, end] = ranges['1 Day'];
        break;
      case '1w':
        [start, end] = ranges['1 Week'];
        break;
      case '2w':
        [start, end] = ranges['2 Weeks'];
        break;
      case '1M':
        [start, end] = ranges['1 Month'];
        break;
      default:
        break; // Invalid input, do nothing
    }
  }

  return { start, end };
}

const labelMatcher = {
  '1d': '1 Day',
  '1w': '1 Week',
  '2w': '2 Weeks',
  '1M': '1 Month',
}

function ConnectedChart({
  inputValue,
  setInputValue,
  ranges,
  runCallback,
  scrollRef,
  id,
}) {
  const calendarRef = useRef(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [internalInputValue, setInternalInputValue] = useState('');
  const dateRangePickerRef = useRef(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  useEffect(() => {
    if (inputValue) {
      const { start, end } = getTimeRanges(inputValue, ranges);
  
      // Compare with current state to prevent unnecessary updates
      if (!internalInputValue?.length) {
        setStartDate(start);
        setEndDate(end);
        if (inputValue.includes(':')) {
          setInternalInputValue(`${start.format('HH:mm DD/MM/YY')} - ${end.format('HH:mm DD/MM/YY')}`);
        } else {
          setInternalInputValue(labelMatcher[inputValue]);
        }
      }
    }
  }, [inputValue]);

  useEffect(() => {
    const dateRangePickerContainer = document.createElement('div');
    dateRangePickerContainer.id = `dateRangePickerContainer-${id}`;
    document.body.appendChild(dateRangePickerContainer);
    const { start: startUnix, end: endUnix } = getTimeRanges(inputValue, ranges);
    const $dateRangePicker = $(dateRangePickerRef.current).daterangepicker({
      startDate: startUnix,
      endDate: endUnix,
      ranges,
      cancelButtonClasses: styles.cancelBtn,
      applyButtonClasses: styles.applyBtn,
      "timePicker": true,
      "timePicker24Hour": true,
      "timePickerSeconds": false,
      "alwaysShowCalendars": true,
      "opens": "center",
      "autoUpdateInput": false,
      "maxDate": moment().endOf('day'),
      "locale": {
        "format": 'HH:mm DD/MM/YYYY',
        "separator": " - ",
        "applyLabel": "Save",
        "cancelLabel": "Cancel",
        "fromLabel": "From",
        "toLabel": "To",
        "customRangeLabel": "Custom",
        "weekLabel": "W",
        "daysOfWeek": [
          "Su",
          "Mo",
          "Tu",
          "We",
          "Th",
          "Fr",
          "Sa"
        ],
        "monthNames": [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December"
        ],
        "firstDay": 1
      },
    }, (start, end, label) => {
      if (label === '1 Day' || label === '1 Week' || label === '2 Weeks' || label === '1 Month') {
        setInternalInputValue(label);
      } else {
        setInternalInputValue(`${start.format('HH:mm DD/MM/YY')} - ${end.format('HH:mm DD/MM/YY')}`);
      }
      setStartDate(start);
      setEndDate(end);
    });

    $dateRangePicker.on('show.daterangepicker', () => {
      setIsCalendarOpen(true);
    });

    $dateRangePicker.on('hide.daterangepicker', () => {
      setIsCalendarOpen(false);
    });

    // Cleanup event listeners on component unmount
    return () => {
      $dateRangePicker.off('show.daterangepicker');
      $dateRangePicker.off('hide.daterangepicker');
      document.body.removeChild(dateRangePickerContainer);
    };
  }, [ranges]);

  useEffect(() => {
    if (startDate && endDate) {
      const startUnix = startDate.unix();
      const endUnix = endDate.unix();
      if (`${startUnix}:${endUnix}` !== inputValue) {
        setInputValue(`${startUnix}:${endUnix}`);
      }
    }
  }, [startDate, endDate, inputValue, setInputValue]);

  useEffect(() => {
    if (internalInputValue !== '' && internalInputValue !== inputValue) {
      runCallback(internalInputValue, startDate, endDate);
    }
  }, [internalInputValue, inputValue, runCallback]);

  useEffect(() => {
    const handleScroll = () => {
      if (!isCalendarOpen) return;
      if (calendarRef.current) {
        const rect = calendarRef.current.getBoundingClientRect();
        const calendarNode = document.querySelector(`#dateRangePickerContainer-${id} + .daterangepicker`);
        if (calendarNode) {
          calendarNode.style.top = `${rect.top + 36}px`;
        }
      }
    };

    if (scrollRef?.current && isCalendarOpen) {
      scrollRef.current.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (scrollRef?.current) {
        scrollRef?.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, [scrollRef, isCalendarOpen]);

  return (
    <div className={styles.inputContainer} ref={calendarRef} style={{ position: 'relative' }}>
      <input
        placeholder='Select date and time range'
        className={
          cn(styles.inputMain, {
            [styles.calendarOpen]: isCalendarOpen,
            [styles.inputFilled]: inputValue?.length
          })}
        ref={dateRangePickerRef}
        value={internalInputValue}
        onChange={(e) => setInternalInputValue(e.target.value)}
      />
      <Calendar className={cn(styles.icon)} />
    </div>
  );
}

export default ConnectedChart;